import RetailModel from 'app/src/data/models/RetailModel';
import PaymentTransactionCollection from 'app/src/data/models/PaymentTransactionCollection';
import URLHelper from 'app/src/data/models/utils/URLHelper';
import Format from 'app/src/data/Format';
import Decimal from 'app/src/data/base/Decimal';
import $ from 'jquery';

import get from 'lodash/get';

// this must match the server-side representation of payment methods
const _Methods = {
  BANK_CARD: 'bank_card',
};

const _Status = {
  PENDING: 'pending',
  REFUNDED: 'refunded',
  VOIDED: 'voided',
  APPROVED: 'approved',
  ERROR: 'error',
  DECLINED: 'declined',
  CANCELLED: 'cancelled',
  UNKNOWN: 'unknown',
};

const _OperationType = {
  SALE: 'sale',
  VOID: 'void',
  REFUND: 'refund',
  /** This is used when the payment's type cannot be derived from its transactions or its workflow */
  UNKNOWN: 'unknown',
};

const _Workflow = {
  INTEGRATED_REFUNDS: 'integrated-refunds',
  TERMINAL_PAYMENTS: 'terminal-payments',
  TERMINAL_REFUNDS: 'terminal-refunds',
  SWIPER_PAYMENTS: 'swiper-payments',
  INTEGRATED_PAYMENTS: 'integrated-payments',
};

const TRANSACTIONS_ATTRIBUTE = 'Transactions';

export default class Payment extends RetailModel {
  static get Methods() {
    return _Methods;
  }

  static get Status() {
    return _Status;
  }

  static get OperationType() {
    return _OperationType;
  }

  static get Workflow() {
    return _Workflow;
  }

  constructor(...args) {
    super(...args);
  }

  // Server uses the optionID field as the model's unique identifier. This
  // informs the Backbone.Model of this fact.
  get idAttribute() {
    return 'paymentID';
  }

  get defaults() {
    return {
      method: '',
      totalAmount: Format.parseDecimal(0),
      saleID: 0,
    };
  }

  get retailResponseName() {
    return 'Payment';
  }

  /**
   * Return the url for the Sale endpoint
   */
  url() {
    let URL = URLHelper.getAccountURL('/Payment');
    if (!this.isNew()) {
      URL += `/${this.id}`;
    }
    return `${URL}.json`;
  }

  /**
   * Overridden to parse out the superfluous json that the LSCloud returns us
   * @param response - the json object returned from the fetch call before this object has initialized from it
   * @returns {*} the json object that this object will initialize itself from
   */
  parse(response) {
    // trim out wonky stuff from the response which is present if we fetch a single model
    // but not if it's coming from a collection
    let payment = super.parse(response);

    // TODO: This is a workaround until single-item lists are returned as JSON arrays by the Cloud API
    if (payment.Transactions && payment.Transactions.PaymentTransaction) {
      if (
        _.isObject(payment.Transactions.PaymentTransaction) &&
        !_.isArray(payment.Transactions.PaymentTransaction) &&
        !_.isEmpty(payment.Transactions.PaymentTransaction)
      ) {
        payment.Transactions.PaymentTransaction = [payment.Transactions.PaymentTransaction];
      }
    }

    // the Cloud API has a tendency to send all values as strings so let's make sure they're of the proper type
    payment.totalAmount = Format.parseDecimal(payment.totalAmount);
    payment.tipAmount = Format.parseDecimal(payment.tipAmount);
    payment.saleID = Format.parseInt(payment.saleID);

    return payment;
  }

  /**
   * Sets the gateway type in the model.
   *
   * @param gatewayID {String}    The gateway type (GatewayConfig.GatewayType)
   */
  set gateway(gatewayID) {
    let cardPaymentRequest = this.get('cardPaymentRequest') || {};
    cardPaymentRequest.gateway = gatewayID;
    this.set('cardPaymentRequest', cardPaymentRequest);
  }

  /**
   * Gets the gateway type stored in the model.
   *
   * @returns {String}    The gateway type (GatewayConfig.GatewayType)
   */
  get gateway() {
    return get(this.get('cardPaymentRequest'), 'gateway', null);
  }

  /**
   * Sets the terminal ID in the model.
   *
   * @param terminalConfigID {String} The terminal ID
   */
  set terminalConfigID(terminalConfigID) {
    let cardPaymentRequest = this.get('cardPaymentRequest') || {};

    cardPaymentRequest.parameters = cardPaymentRequest.parameters || {};
    cardPaymentRequest.parameters.ccTerminalID = terminalConfigID;

    this.set('cardPaymentRequest', cardPaymentRequest);
  }

  /**
   * Gets the terminal ID from the model.
   *
   * @returns {String}    The terminal ID
   */
  get terminalConfigID() {
    return get(this.get('cardPaymentRequest'), 'parameters.ccTerminalID', null);
  }

  /**
   * Sets the workflow in the model.
   *
   * @param workflow {String} The workflow to use for this payment
   */
  set workflow(workflow) {
    let cardPaymentRequest = this.get('cardPaymentRequest') || {};

    cardPaymentRequest.workflow = workflow;

    this.set('cardPaymentRequest', cardPaymentRequest);
  }

  /**
   * Gets the workflow from the model.
   *
   * @returns {String}    The workflow used for this payment
   */
  get workflow() {
    return get(this.get('cardPaymentRequest'), 'workflow', null);
  }

  /**
   * Sets the swipe data in the model.
   *
   * @param swipeData {string} The swipe data
   */
  set swipeData(swipeData) {
    let cardPaymentRequest = this.get('cardPaymentRequest') || {};

    cardPaymentRequest.parameters = cardPaymentRequest.parameters || {};
    cardPaymentRequest.parameters.swipeData = btoa(swipeData);

    this.set('cardPaymentRequest', cardPaymentRequest);
  }

  /**
   * Gets the swipe data from the model.
   *
   * @returns {String}    The swipe data for this payment
   */
  get swipeData() {
    let swipeData = get(this.get('cardPaymentRequest'), 'parameters.swipeData', null);
    return swipeData ? atob(swipeData) : swipeData;
  }

  /**
   * Sets the gateway config ID the model.
   *
   * @param gatewayConfigID {string} The gateway config ID
   */
  set gatewayConfigID(gatewayConfigID) {
    let cardPaymentRequest = this.get('cardPaymentRequest') || {};

    cardPaymentRequest.parameters = cardPaymentRequest.parameters || {};
    cardPaymentRequest.parameters.gatewayConfigID = gatewayConfigID;

    this.set('cardPaymentRequest', cardPaymentRequest);
  }

  /**
   * Sets the card transaction token in the model.
   *
   * @param cardToken {string} The card transaction token
   */
  set cardToken(cardToken) {
    let cardPaymentRequest = this.get('cardPaymentRequest') || {};

    cardPaymentRequest.parameters = cardPaymentRequest.parameters || {};
    cardPaymentRequest.parameters.registrationId = cardToken;

    this.set('cardPaymentRequest', cardPaymentRequest);
  }

  /**
   * Sets the card transaction token in the model.
   *
   * @param cardToken {string} The card transaction token
   */
  set refundCardToken(cardToken) {
    let cardPaymentRequest = this.get('cardPaymentRequest') || {};

    cardPaymentRequest.parameters = cardPaymentRequest.parameters || {};
    cardPaymentRequest.parameters.cardToken = cardToken;

    this.set('cardPaymentRequest', cardPaymentRequest);
  }

  /**
   * Sets the card transaction token in the model.
   *
   * @param cardExpiryDate {string} The card expiry date (MMYY)
   */
  set cardExpiryDate(cardExpiryDate) {
    let cardPaymentRequest = this.get('cardPaymentRequest') || {};

    cardPaymentRequest.parameters = cardPaymentRequest.parameters || {};
    cardPaymentRequest.parameters.cardExpiryDate = cardExpiryDate;

    this.set('cardPaymentRequest', cardPaymentRequest);
  }

  /**
   * Sets the card holder name in the model.
   *
   * @param cardHolderName {string} The card holder name
   */
  set cardHolderName(cardHolderName) {
    let cardPaymentRequest = this.get('cardPaymentRequest') || {};

    cardPaymentRequest.parameters = cardPaymentRequest.parameters || {};
    cardPaymentRequest.parameters.cardHolderName = cardHolderName;

    this.set('cardPaymentRequest', cardPaymentRequest);
  }

  /**
   * Sets the billing postal code in the model.
   *
   * @param postalCode {string} The billing postal code
   */
  set postalCode(postalCode) {
    let cardPaymentRequest = this.get('cardPaymentRequest') || {};

    cardPaymentRequest.parameters = cardPaymentRequest.parameters || {};
    cardPaymentRequest.parameters.billingPostalCode = postalCode;

    this.set('cardPaymentRequest', cardPaymentRequest);
  }

  /**
   * Sets the invoice ID in the model.
   *
   * @param invoiceId {string} The invoice ID
   */
  set invoiceId(invoiceId) {
    let cardPaymentRequest = this.get('cardPaymentRequest') || {};

    cardPaymentRequest.parameters = cardPaymentRequest.parameters || {};
    cardPaymentRequest.parameters.invoiceId = invoiceId;

    this.set('cardPaymentRequest', cardPaymentRequest);
  }

  /**
   * Sets the order ID in the model.
   *
   * @param orderId {string} The order ID
   */
  set orderId(orderId) {
    let cardPaymentRequest = this.get('cardPaymentRequest') || {};

    cardPaymentRequest.parameters = cardPaymentRequest.parameters || {};
    cardPaymentRequest.parameters.orderId = orderId;

    this.set('cardPaymentRequest', cardPaymentRequest);
  }

  /**
   * Gets the gateway config ID from the model.
   *
   * @returns {String}    The gateway config ID for this payment
   */
  get gatewayConfigID() {
    return get(this.get('cardPaymentRequest'), 'parameters.gatewayConfigID', null);
  }

  /**
   * Sets the manual entry allowed flag in the model.
   *
   * @param manualEntryAllowed {Boolean} The manual entry allowed flag
   */
  set isManualEntryAllowed(manualEntryAllowed) {
    let cardPaymentRequest = this.get('cardPaymentRequest') || {};

    cardPaymentRequest.parameters = cardPaymentRequest.parameters || {};
    cardPaymentRequest.parameters.isManualEntryAllowed = manualEntryAllowed;

    this.set('cardPaymentRequest', cardPaymentRequest);
  }

  /**
   * Gets the manual entry allowed flag ID from the model.
   *
   * @returns {Boolean} The manual entry allowed flag
   */
  get isManualEntryAllowed() {
    return get(this.get('cardPaymentRequest'), 'parameters.isManualEntryAllowed', null);
  }

  /**
   * Sets the manual refundedPaymentId in the model.
   *
   * @param refundedPaymentId {String} The refundedPaymentId entry
   */
  set refundedPaymentId(refundedPaymentId) {
    let cardPaymentRequest = this.get('cardPaymentRequest') || {};

    cardPaymentRequest.parameters = cardPaymentRequest.parameters || {};
    cardPaymentRequest.parameters.refundedPaymentId = refundedPaymentId;

    this.set('cardPaymentRequest', cardPaymentRequest);
  }

  /**
   * Gets the manual entry allowed flag ID from the model.
   *
   * @returns {Boolean} The manual entry allowed flag
   */
  get refundedPaymentId() {
    return get(this.get('cardPaymentRequest'), 'parameters.refundedPaymentId', null);
  }

  /**
   * Sets the invoke manual entry flag in the model.
   *
   * @param invokeManualEntry {Boolean} The invoke manual entry flag
   */
  set invokeManualEntry(invokeManualEntry) {
    let cardPaymentRequest = this.get('cardPaymentRequest') || {};

    cardPaymentRequest.parameters = cardPaymentRequest.parameters || {};
    cardPaymentRequest.parameters.invokeManualEntry = invokeManualEntry;

    this.set('cardPaymentRequest', cardPaymentRequest);
  }

  /**
   * Gets the invoke manual entry flag from the model.
   *
   * @returns {Boolean} The invoke manual entry flag
   */
  get invokeManualEntry() {
    return get(this.get('cardPaymentRequest'), 'parameters.invokeManualEntry', null);
  }

  /**
   * Gets the terminal API URL from the model.  This is used to start a transaction
   * with the terminal represented by the model.
   *
   * @returns {String}    The terminal transaction URL
   */
  get terminalApiUrl() {
    return get(this.get('cardPaymentRequest'), 'parameters.terminalApiUrl', null);
  }

  /**
   * Gets the ID of the Payment Transaction to be used for the next update of the Payment.
   * This is used when reporting the response from a terminal interaction to the server.
   *
   * We accomplish updates to a payment via Payment Transaction operations because there can be multiple
   * transactions against a single payment (sale, refund, void).  So, rather than doing a PUT
   * action against the Payment endpoint we instead do a PUT against the PaymentTransaction endpoint which
   * has a reference to the Payment.  The ID returned via this method is the ID to use for PUT against
   * the PaymentTransaction endpoint.
   *
   * @returns {String|null}    The Payment Transaction ID for the request
   */
  get nextPaymentTransactionID() {
    return get(this.get('cardPaymentRequest'), 'parameters.nextPaymentTransactionID', null);
  }

  /**
   * Gets the PaymentTransaction model from the Payment.
   *
   * @returns {PaymentTransaction|null}
   */
  get paymentTransaction() {
    let paymentTransaction = null;
    let paymentTransactions = this.getRelation(
      TRANSACTIONS_ATTRIBUTE,
      PaymentTransactionCollection
    );

    if (paymentTransactions && paymentTransactions.length) {
      paymentTransaction = paymentTransactions.at(0);
    }

    return paymentTransaction;
  }

  /**
   * Gets the error code from the payment transaction.
   *
   * @returns {string}    The status code reported by the payment service
   */
  get errorCode() {
    let errorCode = '';

    if (this.paymentTransaction && this.paymentTransaction.parameters) {
      errorCode = this.paymentTransaction.parameters.statusCode
        ? this.paymentTransaction.parameters.statusCode
        : this.paymentTransaction.get('gatewayReturnCode');
    }

    return errorCode;
  }

  /**
   * Voids the payment
   *
   * @param {string}  terminalID  The ID of the terminal used to void the payment
   * @returns {*}
   */
  voidPayment(terminalID) {
    let deferred = $.Deferred();
    this.terminalConfigID = terminalID;
    this.gateway = this.paymentTransaction.get('gateway');
    this.workflow = _Workflow.TERMINAL_REFUNDS;
    this.set('totalAmount', this.get('totalAmount').negated()); // always want to refund the total amount

    if (this.get('tipAmount')) {
      this.set('tipAmount', this.get('tipAmount').negated());
    }

    if (!this.isNew()) {
      $.ajax(`${URLHelper.getAccountURL('/Payment')}/${this.id}/Refund.json`, {
        type: 'POST',
        contentType: 'application/json',
        dataType: 'json',
        data: JSON.stringify(this.toJSON()),
      })
        .done(response => {
          this.set(this.parse(response));
          deferred.resolve();
        })
        .fail(() => {
          deferred.reject();
        });
    } else {
      console.error('Cannot create a transaction for an unknown payment.');
      deferred.reject();
    }

    return deferred.promise();
  }

  /**
   * Refunds the payment
   *
   * @param {Object} amount  The amount to refund from the original payment.
   * If not specified the entire payment will be refunded.
   * @param {Object} refundTip If true the tipAmount found on the payment will
   * be entirely refunded. If not specified the tipAmount won't be refunded.
   *
   * @returns {Promise}
   */
  refundPayment({ amount, refundTip = false } = {}) {
    let deferred = $.Deferred();
    let transactionModel = this.clone();
    transactionModel.set(
      'totalAmount',
      amount ? amount.negated() : this.get('totalAmount').negated()
    );

    if (window.lsCloud.Modules.hasModule('register.sales.tip') && this.get('tipAmount')) {
      transactionModel.set('tipAmount', refundTip ? this.get('tipAmount').negated() : 0);
    }

    transactionModel.gateway = this.paymentTransaction.get('gateway');
    transactionModel.workflow = _Workflow.INTEGRATED_REFUNDS;

    if (!transactionModel.isNew()) {
      $.ajax(`${URLHelper.getAccountURL('/Payment')}/${this.id}/Refund.json`, {
        type: 'POST',
        contentType: 'application/json',
        dataType: 'json',
        data: JSON.stringify(transactionModel.toJSON()),
      })
        .done(response => {
          this.set(this.parse(response));

          if (response.Payment.status !== 'approved') {
            deferred.reject();
          } else {
            deferred.resolve();
          }
        })
        .fail(() => {
          deferred.reject();
        });
    } else {
      console.error('Cannot create a transaction for an unknown payment.');
      deferred.reject();
    }

    return deferred.promise();
  }
}
